import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/Layout";
import { Plus, Archive, X } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";
import { STATUS_OPTIONS } from "@/lib/statuses";
import { fmtDate } from "@/lib/dates";
import { DatePicker } from "@/components/DatePicker";

const BLANK_FORM = {
  title: "", description: "", todo: "", due_date: "", status: "Not Started",
  stuck_with: "None", stuck_note: "", assignee_ids: [],
};

// Deep-link filter labels. Keys mirror the dashboard "status dots".
const FILTER_META = {
  overdue:     { label: "Overdue",      tone: "red" },
  stuck:       { label: "Stuck",        tone: "amber" },
  in_progress: { label: "In progress",  tone: "navy" },
  not_started: { label: "Not started",  tone: "slate" },
  due_soon:    { label: "Due ≤ 7 days", tone: "amber" },
};

export default function ClientProjectsTab({ clientId, projects, users, canEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [view, setView] = useState("active"); // "active" | "archived"

  // Deep-link filter from the dashboard rings: `/clients/:id?tab=projects&filter=overdue`.
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter");
  const clearFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  };
  // When a filter is set, force the Active toggle (filters only apply to active work).
  useEffect(() => { if (filter) setView("active"); }, [filter]);

  const today = new Date().toISOString().slice(0, 10);
  const sevenDaysOut = (() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  })();

  const matchesFilter = (p) => {
    if (!filter) return true;
    if (p.status === "Completed") return false;
    switch (filter) {
      case "overdue":     return !!p.due_date && p.due_date < today;
      case "due_soon":    return !!p.due_date && p.due_date >= today && p.due_date <= sevenDaysOut;
      case "stuck":       return !!p.stuck_with && p.stuck_with !== "None";
      case "in_progress": return p.status === "In Progress";
      case "not_started": return p.status === "Not Started";
      default:            return true;
    }
  };

  const { active, archived } = useMemo(() => {
    const a = []; const r = [];
    for (const p of projects) {
      (p.status === "Completed" ? r : a).push(p);
    }
    return { active: a, archived: r };
  }, [projects]);

  const shown = (view === "archived" ? archived : active).filter(matchesFilter);

  const create = async () => {
    try {
      await api.post("/projects", { ...form, client_id: clientId });
      toast.success("Project created");
      setOpen(false);
      setForm(BLANK_FORM);
      onChanged?.();
    } catch (e) {
      toast.error(parseApiError(e, "Failed"));
    }
  };

  return (
    <>
      {filter && FILTER_META[filter] && (
        <div className="mb-4 flex items-center gap-2 fade-in-up" data-testid="projects-filter-pill">
          <span className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Filter:</span>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
            FILTER_META[filter].tone === "red" ? "bg-red-50 text-red-700 border border-red-200"
              : FILTER_META[filter].tone === "amber" ? "bg-amber-50 text-amber-700 border border-amber-200"
              : FILTER_META[filter].tone === "navy" ? "bg-[#0a2540]/5 text-[#0a2540] border border-[#0a2540]/20"
              : "bg-slate-50 text-slate-600 border border-slate-200"
          }`}>
            {FILTER_META[filter].label}
            <span className="font-mono-data opacity-70">· {shown.length}</span>
            <button onClick={clearFilter} className="hover:opacity-70" data-testid="clear-projects-filter" aria-label="Clear filter">
              <X size={12} />
            </button>
          </span>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden" data-testid="project-view-toggle">
          <button
            onClick={() => setView("active")}
            className={`px-3 py-1.5 text-xs ${view === "active" ? "bg-[#0a2540] text-white" : "bg-white text-slate-600 hover:text-[#0a2540]"}`}
            data-testid="view-active"
          >
            Active <span className="ml-1 font-mono-data opacity-80">({active.length})</span>
          </button>
          <button
            onClick={() => setView("archived")}
            className={`px-3 py-1.5 text-xs inline-flex items-center gap-1 ${view === "archived" ? "bg-[#0a2540] text-white" : "bg-white text-slate-600 hover:text-[#0a2540]"}`}
            data-testid="view-archived"
          >
            <Archive size={11} /> Archived <span className="ml-1 font-mono-data opacity-80">({archived.length})</span>
          </button>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" data-testid="btn-new-project">
                <Plus size={14} className="mr-1.5" /> New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl rounded-sm">
              <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>New Project</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Title *</Label>
                  <Input className="mt-1 rounded-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="input-project-title" />
                </div>
                <div>
                  <Label className="text-xs">Description</Label>
                  <Textarea className="mt-1 rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="input-project-description" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Due Date</Label>
                    <div className="mt-1"><DatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testId="input-project-due" /></div>
                  </div>
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger className="mt-1 rounded-sm" data-testid="select-project-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">To-Do / Scope</Label>
                  <Textarea className="mt-1 rounded-sm" value={form.todo} onChange={(e) => setForm({ ...form, todo: e.target.value })} data-testid="input-project-todo" />
                </div>
                <div>
                  <Label className="text-xs">Assignees</Label>
                  <div className="mt-1 border border-slate-200 rounded-sm p-2 max-h-40 overflow-y-auto space-y-1">
                    {users.map((u) => (
                      <label key={u.user_id} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.assignee_ids.includes(u.user_id)}
                          onChange={(e) => {
                            const ids = new Set(form.assignee_ids);
                            if (e.target.checked) ids.add(u.user_id); else ids.delete(u.user_id);
                            setForm({ ...form, assignee_ids: [...ids] });
                          }}
                          data-testid={`assignee-${u.user_id}`}
                        />
                        {u.name} <span className="text-slate-400 font-mono-data">({u.role})</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={create} data-testid="btn-create-project">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {view === "archived" && archived.length > 0 && (
        <div className="mb-3 text-[11px] text-slate-500 inline-flex items-center gap-1.5">
          <Archive size={11} /> Archived projects are read-only here. Open one to view details or re-open it.
        </div>
      )}

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2" data-testid="project-cards-mobile">
        {shown.length === 0 && (
          <div className="border border-slate-200 px-4 py-10 text-center text-xs text-slate-400">
            {view === "archived" ? "No archived projects" : "No projects yet"}
          </div>
        )}
        {shown.map((p) => (
          <Link
            key={p.id}
            to={`/projects/${p.id}`}
            className="block border border-slate-200 px-3 py-2.5 hover:bg-slate-50"
            data-testid={`project-card-${p.id}`}
          >
            <div className="text-sm font-medium text-[#0a2540] line-clamp-2">{p.title}</div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px]">
              <StatusBadge status={p.status} />
              {p.stuck_with && p.stuck_with !== "None" && (
                <span className="px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200">
                  Stuck — {p.stuck_with}
                </span>
              )}
              {view === "archived" && p.completed_at ? (
                <span className="font-mono-data text-emerald-700">Completed {fmtDate(p.completed_at)}</span>
              ) : (
                <span className="font-mono-data text-slate-500">{fmtDate(p.due_date)}</span>
              )}
              <span className="text-slate-400">·</span>
              <span className="text-slate-600">{(p.assignee_ids || []).length} assignees</span>
            </div>
          </Link>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2.5">Project</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-left px-4 py-2.5">Stuck With</th>
              <th className="text-left px-4 py-2.5">{view === "archived" ? "Completed" : "Due"}</th>
              <th className="text-left px-4 py-2.5">Assignees</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-xs text-slate-400">
                {view === "archived" ? "No archived projects" : "No projects yet"}
              </td></tr>
            )}
            {shown.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`project-row-${p.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/projects/${p.id}`} className="font-medium text-[#0a2540] hover:underline">{p.title}</Link>
                </td>
                <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-3 text-xs text-slate-600">{p.stuck_with === "None" ? "—" : p.stuck_with}</td>
                <td className="px-4 py-3 font-mono-data text-xs">
                  {view === "archived"
                    ? <span className="text-emerald-700">{fmtDate(p.completed_at)}</span>
                    : fmtDate(p.due_date)}
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{(p.assignee_ids || []).length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
